import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
} from "@chakra-ui/modal";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import usePostAxiosData from "hooks/usePostAxiosData";
import toast from "react-hot-toast";
import InputField from "components/fields/InputField";
import Error from "components/message/Error";
import { Button } from "@chakra-ui/react";
import { config } from "variables/config";
import useDataFetching from "hooks/useDataFetching";
import { useTranslation } from "react-i18next"; // Importer useTranslation

const AddUser = (props) => {
  const { isOpen, onClose } = props;
  const { t } = useTranslation("add_user"); // Utiliser le namespace

  /** Form validation config */
  const schema = yup.object().shape({
    firstname: yup.string().required(t("firstname_required")), // Traduit
    lastname: yup.string().required(t("lastname_required")),   // Traduit
    phone: yup.string().required(t("phone_required")),         // Traduit
    password: yup.string(),
    email: yup.string().email(t("email_invalid")),             // Traduit
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmitHandler = (data) => {
    handleSave(data);
  };

  /** post create user */
  const [loading, postAxiosData, errorMassage, successMessage, result] =
    usePostAxiosData(`auth/register`, "POST");
  const [regionId, setRegionId] = useState(""); // Initialiser avec une chaîne vide

  const handleSave = async (dataYub) => {
    const data = {
      firstname: dataYub?.firstname,
      lastname: dataYub?.lastname,
      phone: dataYub?.phone,
      email: dataYub?.email,
      password: dataYub?.password,
      regionId,
      roles: ["AGENT"],
    };

    await postAxiosData(data);
  };

  /** gestion des erreurs et succès */
  useEffect(() => {
    if (successMessage?.status === 200 || successMessage?.status === 201) {
      reset();
      toast.success(
        `${successMessage?.message ? successMessage?.message : t("success_message")}`, // Traduit
        {
          duration: 4000,
          className: "bg-success text-white",
        }
      );
      props?.endFetch();
      onClose();
    }
  }, [successMessage, reset, props, onClose, t]); // Ajouter t aux dépendances

  const [loadingR, errorR, regions, fetchDataR] = useDataFetching(
    `${config.app.api_url}/regions`
  );

  return (
    <>
      <AlertDialog isOpen={isOpen} onClose={onClose} isCentered={true}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              <div className="text-center">
                <span className="text-3xl uppercase">{t("add_account_title")}</span> {/* Traduit */}
              </div>
            </AlertDialogHeader>

            <AlertDialogBody>
              <form onSubmit={handleSubmit(onSubmitHandler)}>
                <div className="my-4 grid gap-2 md:grid-cols-1">
                  {errorMassage?.message && (
                    <Error
                      message={
                        errorMassage?.message !== "" && errorMassage?.message
                      }
                    />
                  )}
                  <div>
                    <div className="mb-2">
                      <label
                        htmlFor={"region"}
                        className={`ml-3 text-sm font-bold text-navy-700 dark:text-white`}
                      >
                        {t("region_label")} {/* Traduit */}
                      </label>
                      <select
                        onChange={(e) => {
                          setRegionId(e.target.value);
                        }}
                        className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white p-3 text-sm outline-none"
                        name="region"
                        id="region"
                        required={true}
                      >
                        <option value="" disabled selected>
                          {t("select_region_placeholder")} {/* Traduit */}
                        </option>
                        {regions?.data &&
                          regions.data.map((item) => {
                            return <option key={item?.id} value={item?.id}>{item?.name}</option>;
                          })}
                      </select>
                    </div>

                    <InputField
                      register={register}
                      variant="auth"
                      extra="mb-3"
                      label={t("firstname_label")}
                      required={true}
                      placeholder={t("firstname_placeholder")}
                      id="firstname"
                      name="firstname"
                      type="text"
                      error={errors.firstname?.message} // Simplifié
                    />
                  </div>
                  <div>
                    <InputField
                      register={register}
                      variant="auth"
                      extra="mb-3"
                      label={t("lastname_label")}
                      required={true}
                      placeholder={t("lastname_placeholder")}
                      id="lastname"
                      name="lastname"
                      type="text"
                      error={errors.lastname?.message} // Simplifié
                    />
                  </div>
                  <div>
                    <InputField
                      register={register}
                      variant="auth"
                      extra="mb-3"
                      label={t("phone_label")}
                      required={true}
                      placeholder={t("phone_placeholder")}
                      id="phone"
                      name="phone"
                      type="text"
                      error={errors.phone?.message} // Simplifié
                    />
                  </div>
                  <div>
                    <InputField
                      register={register}
                      variant="auth"
                      extra="mb-3"
                      label={t("email_label")}
                      placeholder={t("email_placeholder")}
                      id="email"
                      name="email"
                      type="email"
                      error={errors.email?.message} 
                    />
                  </div>
                  <div>
                    <InputField
                      register={register}
                      variant="auth"
                      extra="mb-3"
                      label={t("password_label")}
                      placeholder={t("password_placeholder")}
                      id="password"
                      name="password"
                      type="password"
                      error={errors.password?.message}
                    />
                  </div>
                </div>
                <div className="flex justify-between p-4">
                  <div>
                    <Button
                      onClick={() => {
                        onClose();
                      }}
                    >
                      {t("close_button")}
                    </Button>
                  </div>
                  <div>
                    <Button type="submit" colorScheme="green">
                      {t("save_button")} 
                    </Button>
                  </div>
                </div>
              </form>
            </AlertDialogBody>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default AddUser;